import { useCallback, useMemo } from 'react';
import { useToasts } from 'stateful-ui/src';
import { useIntegratedMutation } from 'stateful-ui/src/hooks';
import { useOrganization } from 'stateful-ui/src/graphql/community/hooks/useOrganization';
import {
  GetCommunityAttachmentsQuery,
  GetCommunityAttachmentsQueryVariables,
  UpdateAttachmentPathMutation,
  UpdateAttachmentPathMutationVariables,
} from 'generated/gql';
import UPDATE_ATTACHMENT_PATH from 'app/attachments/graphql/updateAttachmentPath';
import GET_ATTACHMENTS from 'app/attachments/graphql/getAttachments';
import {
  ATTACHMENTS_APPLICATION,
  ATTACHMENTS_ID,
  ATTACHMENTS_TYPE,
} from '../constants';

export const useUpdateAttachmentPath = () => {
  const { data: organization } = useOrganization();
  const { pushToast } = useToasts();
  const { performMutation, loading, error } = useIntegratedMutation<
    UpdateAttachmentPathMutation,
    UpdateAttachmentPathMutationVariables
  >(UPDATE_ATTACHMENT_PATH, 'attachments');

  const updateAttachmentPath = useCallback(
    async (attachmentId: string, folderPath?: string | null) => {
      if (!organization) {
        throw new Error('organization not available');
      }

      try {
        const owner = organization.split('/')[0];
        const { data } = await performMutation({
          variables: {
            input: {
              owner,
              application: ATTACHMENTS_APPLICATION,
              parentType: ATTACHMENTS_TYPE,
              parentId: ATTACHMENTS_ID,
              attachmentId,
              folderPath: folderPath || null,
            },
          },
          update: (cache, { data: mutationData }) => {
            if (!mutationData?.updateAttachmentPath) return;

            cache.writeQuery<
              GetCommunityAttachmentsQuery,
              GetCommunityAttachmentsQueryVariables
            >({
              query: GET_ATTACHMENTS,
              variables: {
                input: {
                  application: ATTACHMENTS_APPLICATION,
                  parentType: ATTACHMENTS_TYPE,
                  parentId: ATTACHMENTS_ID,
                },
              },
              data: {
                getCommunityAttachments: mutationData.updateAttachmentPath,
              },
            });
          },
        });

        return data?.updateAttachmentPath || undefined;
      } catch {
        pushToast({
          variables: {
            toast: {
              message: 'The document folder could not be updated',
            },
          },
        });
        throw new Error('update attachment path failed');
      }
    },
    [organization, performMutation, pushToast],
  );

  return useMemo(
    () => ({
      updateAttachmentPath,
      loading,
      error: error?.message,
    }),
    [updateAttachmentPath, loading, error],
  );
};
